import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { bg } from "date-fns/locale";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarDays, Clock, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Calendar } from "../ui/calendar";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { services } from "../../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Availability rules per service.
// From 1 November 2026 onwards, Thursday and Friday slots change for individual therapy.
const NOV_FIRST_2026 = new Date(2026, 10, 1); // month is 0-indexed; 10 = November

function timesForService(serviceId, date) {
  if (!serviceId || !date) return [];
  const dow = date.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const afterNov = date >= NOV_FIRST_2026;

  if (serviceId === "individual") {
    if (dow === 5) {
      // Петък — от ноември 2026 нататък: 14:00, 15:30, 16:00; преди това: 15:00, 16:30
      return afterNov ? ["14:00", "15:30", "16:00"] : ["15:00", "16:30"];
    }
    if (dow === 4 && afterNov) {
      // Четвъртък (само от ноември 2026 нататък)
      return ["10:15", "14:15"];
    }
    return [];
  }

  if (serviceId === "online") {
    if (dow === 2 || dow === 3) return ["16:30", "18:00"]; // вторник, сряда
    return [];
  }

  return [];
}

// Only individual & online can be booked here (group therapy is excluded)
const BOOKABLE_SERVICE_IDS = ["individual", "online"];
const bookableServices = services.filter((s) => BOOKABLE_SERVICE_IDS.includes(s.id));

const emptyForm = { name: "", email: "", phone: "", message: "" };

export default function BookingDialog({ open, onOpenChange, preselectedService }) {
  const [step, setStep] = useState(1);
  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [confirmed, setConfirmed] = useState(null);
  const [takenSlots, setTakenSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      const initial = BOOKABLE_SERVICE_IDS.includes(preselectedService)
        ? preselectedService
        : "";
      setServiceId(initial);
      setDate(null);
      setSlot("");
      setForm(emptyForm);
      setConfirmed(null);
      setTakenSlots([]);
      setSubmitting(false);
    }
  }, [open, preselectedService]);

  // When date changes reset the picked time slot and load taken slots
  useEffect(() => {
    setSlot("");
    if (!date) {
      setTakenSlots([]);
      return;
    }
    let active = true;
    const dateStr = format(date, "yyyy-MM-dd");
    axios
      .get(`${API}/bookings/availability/${dateStr}`)
      .then((res) => {
        if (active) setTakenSlots(res.data.takenSlots || []);
      })
      .catch(() => {
        if (active) setTakenSlots([]);
      });
    return () => {
      active = false;
    };
  }, [date]);

  // Reset date + slot when service changes (rules differ per service)
  useEffect(() => {
    setDate(null);
    setSlot("");
  }, [serviceId]);

  const selectedService = useMemo(
    () => services.find((s) => s.id === serviceId),
    [serviceId]
  );

  const availableTimes = useMemo(
    () => timesForService(serviceId, date),
    [serviceId, date]
  );

  const disabledDays = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (day < today) return true;
    // If a service is picked, disable days where the service has no availability
    if (serviceId) {
      return timesForService(serviceId, day).length === 0;
    }
    // No service picked yet — disable all so user picks service first
    return true;
  };

  const canContinue = serviceId && date && slot;
  const canSubmit = form.name.trim() && form.email.trim() && form.phone.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        serviceId,
        serviceTitle: selectedService?.title,
        date: format(date, "yyyy-MM-dd"),
        time: slot,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message,
      });
      setConfirmed(res.data);
      setStep(3);
      toast.success("Заявката е изпратена", {
        description: `${format(date, "d MMMM yyyy", { locale: bg })} · ${slot} ч.`,
      });
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Часът вече е зает", {
          description: "Моля, върнете се и изберете друг час.",
        });
      } else if (err.response?.status === 422) {
        toast.error("Невалидни данни", {
          description: "Моля, проверете попълнените полета.",
        });
      } else {
        toast.error("Възникна грешка", {
          description: "Моля, опитайте отново след малко.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ background: "var(--cream)", border: "1px solid var(--line)" }}
      >
        {step !== 3 && (
          <DialogHeader>
            <DialogTitle
              style={{
                fontFamily: "var(--font-serif)",
                fontWeight: 300,
                fontSize: "1.9rem",
                letterSpacing: "0.005em",
              }}
            >
              Нека поговорим
            </DialogTitle>
            <DialogDescription className="annotation" style={{ fontSize: "1.05rem" }}>
              {step === 1
                ? "(изберете услуга, дата и час)"
                : "(още няколко думи за вас)"}
            </DialogDescription>
          </DialogHeader>
        )}

        {step === 1 && (
          <div className="space-y-5">
            <div>
              <Label className="label-mono" style={{ color: "var(--terra)" }}>
                Услуга
              </Label>
              <Select value={serviceId} onValueChange={setServiceId}>
                <SelectTrigger className="mt-2 bg-white/60" data-testid="booking-service-select">
                  <SelectValue placeholder="Изберете услуга" />
                </SelectTrigger>
                <SelectContent>
                  {bookableServices.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`booking-service-${s.id}`}>
                      {[s.title, s.duration, s.price].filter(Boolean).join(" · ")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p
                className="annotation"
                style={{ marginTop: "0.6rem", fontSize: "0.9rem", color: "var(--ink-soft)" }}
              >
                (груповата терапия се записва чрез директен контакт)
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-[auto_1fr]">
              <div>
                <Label className="label-mono flex items-center gap-2" style={{ color: "var(--terra)" }}>
                  <CalendarDays size={13} /> Дата
                </Label>
                <div className="mt-2 border rounded-md bg-white/60" style={{ borderColor: "var(--line)" }}>
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={disabledDays}
                    locale={bg}
                    weekStartsOn={1}
                    data-testid="booking-calendar"
                  />
                </div>
                {!serviceId && (
                  <p className="annotation mt-3" style={{ fontSize: "0.9rem", color: "var(--ink-soft)" }}>
                    (първо изберете услуга)
                  </p>
                )}
              </div>

              <div>
                <Label className="label-mono flex items-center gap-2" style={{ color: "var(--terra)" }}>
                  <Clock size={13} /> Час
                </Label>
                <div className="mt-2 grid grid-cols-2 gap-2" data-testid="booking-times">
                  {availableTimes.length === 0 && (
                    <p
                      className="annotation col-span-2"
                      style={{ fontSize: "0.92rem", color: "var(--ink-soft)" }}
                    >
                      {!serviceId
                        ? "(изберете услуга и дата)"
                        : !date
                        ? "(изберете дата от календара)"
                        : "(няма свободни часове за тази дата)"}
                    </p>
                  )}
                  {availableTimes.map((t) => {
                    const isTaken = takenSlots.includes(t);
                    const disabled = isTaken;
                    return (
                      <button
                        key={t}
                        type="button"
                        className={`booking-slot ${slot === t ? "selected" : ""}`}
                        onClick={() => setSlot(t)}
                        disabled={disabled}
                        title={isTaken ? "Часът е зает" : undefined}
                        data-testid={`booking-slot-${t}`}
                        style={
                          disabled
                            ? {
                                opacity: 0.35,
                                cursor: "not-allowed",
                                textDecoration: "line-through",
                              }
                            : {}
                        }
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <Button
              className="w-full h-12 text-xs tracking-[0.22em] uppercase font-bold"
              style={{ background: "var(--ink)", color: "var(--cream)", borderRadius: 2 }}
              disabled={!canContinue}
              onClick={() => setStep(2)}
              data-testid="booking-continue-btn"
            >
              Продължете
            </Button>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div
              className="rounded-md px-4 py-3 text-sm"
              style={{ background: "var(--cream-soft)", border: "1px solid var(--line)" }}
            >
              <strong>{selectedService?.title}</strong> ·{" "}
              {format(date, "d MMMM yyyy (EEEE)", { locale: bg })} · {slot} ч.
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="bk-name">Име и фамилия *</Label>
                <Input
                  id="bk-name"
                  className="mt-1.5 bg-white/60"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Мария Иванова"
                  required
                />
              </div>
              <div>
                <Label htmlFor="bk-phone">Телефон *</Label>
                <Input
                  id="bk-phone"
                  className="mt-1.5 bg-white/60"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+359 88 000 0000"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="bk-email">Имейл *</Label>
              <Input
                id="bk-email"
                type="email"
                className="mt-1.5 bg-white/60"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="maria@example.com"
                required
              />
            </div>
            <div>
              <Label htmlFor="bk-msg">Съобщение (незадължително)</Label>
              <Textarea
                id="bk-msg"
                className="mt-1.5 bg-white/60"
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Споделете накратко какво ви води при мен…"
              />
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="h-12 flex-1 text-xs tracking-[0.22em] uppercase font-bold"
                style={{ borderRadius: 2 }}
                onClick={() => setStep(1)}
              >
                Назад
              </Button>
              <Button
                type="submit"
                className="h-12 flex-[2] text-xs tracking-[0.22em] uppercase font-bold"
                style={{ background: "var(--ink)", color: "var(--cream)", borderRadius: 2 }}
                disabled={!canSubmit || submitting}
                data-testid="booking-submit-btn"
              >
                {submitting ? "Изпращане…" : "Изпратете заявка"}
              </Button>
            </div>
          </form>
        )}

        {step === 3 && confirmed && (
          <div className="text-center py-8 space-y-4">
            <CheckCircle2 size={52} className="mx-auto" style={{ color: "var(--terra)" }} />
            <h3
              style={{
                fontFamily: "var(--font-serif)",
                fontWeight: 300,
                fontSize: "2rem",
                letterSpacing: "0.005em",
              }}
            >
              Благодаря ви, {confirmed.name.split(" ")[0]}!
            </h3>
            <p style={{ color: "var(--ink-soft)", maxWidth: "38ch", margin: "0 auto", lineHeight: 1.7 }}>
              Заявката ви за <strong>{confirmed.serviceTitle}</strong> на{" "}
              <strong>{format(new Date(confirmed.date), "d MMMM yyyy", { locale: bg })}, {confirmed.time} ч.</strong>{" "}
              е приета. Ще се свържа с вас до 24 часа за потвърждение.
            </p>
            <p className="annotation" style={{ fontSize: "1.05rem", color: "var(--ink-soft)" }}>
              (очаквам срещата ни)
            </p>
            <Button
              className="h-11 px-8 text-xs tracking-[0.22em] uppercase font-bold"
              style={{ background: "var(--ink)", color: "var(--cream)", borderRadius: 2 }}
              onClick={() => onOpenChange(false)}
            >
              Затворете
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
